import { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CATEGORIAS_PUBLICACAO } from '../publicacao.definitions';
import type { AnexoPublicacao, CategoriaPublicacao } from '../publicacao.types';
import { publicacaoService } from '../publicacaoService';
import { ChipArquivo, EnvioArquivo } from '../components/EnvioArquivo';
import type { FormPublicacao } from './publicacaoEditor';
import { DocumentoRelacionadoCampo } from './DocumentoRelacionadoCampo';

interface EditorConteudoProps {
  form: FormPublicacao;
  /** Aceita função (atualização a partir do estado atual) — vários envios terminam fora de ordem. */
  onChange: (parcial: Partial<FormPublicacao> | ((atual: FormPublicacao) => Partial<FormPublicacao>)) => void;
  /** Id da publicação (já reservado se for nova) — pasta dos arquivos no Storage. */
  publicacaoId: string;
}

const ROTULO = 'block text-sm font-medium text-raro-gray-700';

/** Título, categoria, capa, resumo do card, texto (Markdown com pré-visualização) e anexos. Capa e
 * anexos sobem na hora (barra + ✓ "Enviado") e têm X pra remover. */
export function EditorConteudo({ form, onChange, publicacaoId }: EditorConteudoProps) {
  const [recemEnviados, setRecemEnviados] = useState<string[]>([]);

  /** Enviado nesta edição: apaga já. Já salvo antes: apaga do Storage só ao salvar. */
  const descartar = (path: string | null): Partial<FormPublicacao> => {
    if (!path) return {};
    if (form.pathsEnviados.includes(path)) {
      void publicacaoService.removerArquivo(path);
      return { pathsEnviados: form.pathsEnviados.filter((p) => p !== path) };
    }
    return { pathsRemovidos: [...form.pathsRemovidos, path] };
  };

  const marcarRecem = (anexo: AnexoPublicacao) => setRecemEnviados((atual) => [...atual, anexo.path]);

  return (
    <div className="space-y-4">
      <label className="block">
        <span className={ROTULO}>Título *</span>
        <input
          className="input-primary mt-1 w-full"
          maxLength={140}
          value={form.titulo}
          onChange={(event) => onChange({ titulo: event.target.value })}
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={ROTULO}>Categoria *</span>
          <select
            className="input-primary mt-1 w-full"
            value={form.categoria}
            onChange={(event) => onChange({ categoria: event.target.value as CategoriaPublicacao | '' })}
          >
            <option value="">Selecione</option>
            {CATEGORIAS_PUBLICACAO.map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <div>
          <span className={ROTULO}>Capa</span>
          <div className="mt-1">
            {form.capaUrl ? (
              <div className="relative">
                <img
                  src={form.capaUrl}
                  alt=""
                  className={`aspect-[16/7] w-full rounded-lg object-cover ${
                    form.capaPath && recemEnviados.includes(form.capaPath) ? 'ring-2 ring-green-400' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => onChange({ capaUrl: null, capaPath: null, ...descartar(form.capaPath) })}
                  className="absolute right-2 top-2 rounded-full bg-white/90 p-1 text-raro-gray-600 shadow hover:text-rose-600"
                  aria-label="Remover capa"
                  title="Remover capa"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
                {form.capaPath && recemEnviados.includes(form.capaPath) && (
                  <span className="absolute bottom-2 left-2 rounded-full bg-green-600 px-2 py-0.5 text-xs font-semibold text-white">
                    ✓ Enviada
                  </span>
                )}
              </div>
            ) : (
              <EnvioArquivo
                publicacaoId={publicacaoId}
                pasta="capa"
                accept="image/png,image/jpeg,image/webp"
                texto="Enviar imagem (sem imagem, usa a cor da categoria)"
                onEnviado={(anexo) => {
                  marcarRecem(anexo);
                  onChange((atual) => ({ capaUrl: anexo.url, capaPath: anexo.path, pathsEnviados: [...atual.pathsEnviados, anexo.path] }));
                }}
              />
            )}
          </div>
        </div>
      </div>

      <label className="block">
        <span className={ROTULO}>Resumo do card *</span>
        <input
          className="input-primary mt-1 w-full"
          maxLength={200}
          placeholder="Uma frase que aparece no card da Início"
          value={form.resumo}
          onChange={(event) => onChange({ resumo: event.target.value })}
        />
      </label>

      <div>
        <span className={ROTULO}>Texto *</span>
        <div className="mt-1" data-color-mode="light">
          <MDEditor value={form.conteudo} onChange={(valor) => onChange({ conteudo: valor ?? '' })} height={260} preview="edit" />
        </div>
        <p className="mt-1 text-xs text-raro-gray-500">Use a barra para negrito, listas e links; o ícone de olho mostra a pré-visualização.</p>
      </div>

      <div className="space-y-2">
        <span className={ROTULO}>
          Anexos <span className="font-normal text-raro-gray-500">(arquivos só deste post)</span>
        </span>
        {form.anexos.map((anexo) => (
          <ChipArquivo
            key={anexo.path}
            anexo={anexo}
            recemEnviado={recemEnviados.includes(anexo.path)}
            onRemover={() => onChange({ anexos: form.anexos.filter((a) => a.path !== anexo.path), ...descartar(anexo.path) })}
          />
        ))}
        <EnvioArquivo
          publicacaoId={publicacaoId}
          pasta="anexos"
          multiplo
          accept="application/pdf,image/png,image/jpeg,image/webp"
          texto="Adicionar PDF ou imagem (até 15 MB cada)"
          onEnviado={(anexo) => {
            marcarRecem(anexo);
            onChange((atual) => ({ anexos: [...atual.anexos, anexo], pathsEnviados: [...atual.pathsEnviados, anexo.path] }));
          }}
        />
        {form.anexos.length > 0 && (
          <p className="text-xs text-raro-gray-500">Se a publicação pedir ciência, a pessoa só confirma depois de abrir os anexos.</p>
        )}
      </div>

      <DocumentoRelacionadoCampo form={form} onChange={onChange} />
    </div>
  );
}
